import React, { useState, useEffect } from 'react';
import api from '../services/api';

const ConfiguracionFinanciera = ({ proyectoId, onClose, onActualizar }) => {
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState({ texto: '', tipo: '' });
  
  const [config, setConfig] = useState({
    valor_contrato: 0,
    presupuesto_materiales: 0,
    presupuesto_mano_obra: 0,
    presupuesto_indirectos: 0,
    usa_control_financiero: false
  });

  useEffect(() => {
    cargarConfiguracion();
  }, [proyectoId]);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/proyectos/${proyectoId}/financiero/configuracion`);
      
      setConfig({
        valor_contrato: Number(response.data.valor_contrato) || 0,
        presupuesto_materiales: Number(response.data.presupuesto) || 0,
        presupuesto_mano_obra: Number(response.data.presupuesto_mano_obra) || 0,
        presupuesto_indirectos: Number(response.data.presupuesto_indirectos) || 0,
        usa_control_financiero: response.data.usa_control_financiero || false
      });
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      setMensaje({ texto: '❌ Error al cargar configuración', tipo: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ FUNCIÓN CORREGIDA - Sin pérdida de precisión
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setConfig(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      // ✅ Usar Number() en lugar de parseFloat()
      // ✅ Mantener como string mientras se escribe, convertir solo al guardar
      const numValue = value === '' ? 0 : Number(value);
      
      setConfig(prev => ({
        ...prev,
        [name]: numValue
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setGuardando(true);
      
      // ✅ Asegurar que los valores sean números con 2 decimales
      const configToSend = {
        ...config,
        valor_contrato: Number(config.valor_contrato).toFixed(2),
        presupuesto_materiales: Number(config.presupuesto_materiales).toFixed(2),
        presupuesto_mano_obra: Number(config.presupuesto_mano_obra).toFixed(2),
        presupuesto_indirectos: Number(config.presupuesto_indirectos).toFixed(2)
      };
      
      await api.put(`/proyectos/${proyectoId}/financiero/configuracion`, configToSend);
      
      setMensaje({ texto: '✅ Configuración guardada exitosamente', tipo: 'success' });
      
      if (onActualizar) {
        onActualizar();
      }
      
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error) {
      console.error('Error al guardar:', error);
      setMensaje({ texto: '❌ Error al guardar configuración', tipo: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  // ✅ CÁLCULOS CORREGIDOS - Usar Number() consistentemente
  const calcularPresupuestoTotal = () => {
    return Number(config.presupuesto_materiales || 0) + 
           Number(config.presupuesto_mano_obra || 0) + 
           Number(config.presupuesto_indirectos || 0);
  };

  const calcularGananciaEstimada = () => {
    return Number(config.valor_contrato || 0) - calcularPresupuestoTotal();
  };

  const calcularMargenEstimado = () => {
    const valorContrato = Number(config.valor_contrato || 0);
    if (valorContrato === 0) return 0;
    return ((calcularGananciaEstimada() / valorContrato) * 100).toFixed(2);
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>💼 Configuración Financiera del Proyecto</h2>
        <button onClick={onClose} style={styles.btnClose}>✕</button>
      </div>

      {mensaje.texto && (
        <div style={{
          ...styles.mensaje,
          background: mensaje.tipo === 'success' ? '#dcfce7' : '#fee2e2',
          color: mensaje.tipo === 'success' ? '#166534' : '#991b1b'
        }}>
          {mensaje.texto}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Activar Control Financiero */}
        <div style={styles.checkboxGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="usa_control_financiero"
              checked={config.usa_control_financiero}
              onChange={handleChange}
              style={styles.checkbox}
            />
            <span style={styles.checkboxText}>
              Activar control financiero avanzado
            </span>
          </label>
          <p style={styles.checkboxHelp}>
            Permite registrar gastos de nómina, combustible, viáticos y otros costos operativos
          </p>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📊 Presupuesto del Proyecto</h3>

          {/* Valor del Contrato */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Valor del Contrato</label>
            <input
              type="number"
              name="valor_contrato"
              value={config.valor_contrato}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={styles.input}
              placeholder="0.00"
            />
            <span style={styles.currency}>RD$</span>
          </div>

          {/* Presupuesto de Materiales */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Presupuesto de Materiales</label>
            <input
              type="number"
              name="presupuesto_materiales"
              value={config.presupuesto_materiales}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={styles.input}
              placeholder="0.00"
            />
            <span style={styles.currency}>RD$</span>
          </div>

          {/* Presupuesto de Mano de Obra */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Presupuesto de Mano de Obra</label>
            <input
              type="number"
              name="presupuesto_mano_obra"
              value={config.presupuesto_mano_obra}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={styles.input}
              placeholder="0.00"
            />
            <span style={styles.currency}>RD$</span>
          </div>

          {/* Presupuesto de Gastos Indirectos */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Presupuesto de Gastos Indirectos</label>
            <input
              type="number"
              name="presupuesto_indirectos"
              value={config.presupuesto_indirectos}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={styles.input}
              placeholder="0.00"
            />
            <span style={styles.currency}>RD$</span>
            <p style={styles.inputHelp}>
              Combustible, viáticos, comidas, herramientas, etc.
            </p>
          </div>
        </div>

        {/* Resumen */}
        <div style={styles.summary}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Presupuesto Total:</span>
            <span style={styles.summaryValue}>
              RD$ {calcularPresupuestoTotal().toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Ganancia Estimada:</span>
            <span style={{
              ...styles.summaryValue,
              color: calcularGananciaEstimada() >= 0 ? '#059669' : '#dc2626'
            }}>
              RD$ {calcularGananciaEstimada().toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.summaryLabel}>Margen Estimado:</span>
            <span style={{
              ...styles.summaryValue,
              color: calcularMargenEstimado() >= 15 ? '#059669' : calcularMargenEstimado() >= 10 ? '#f59e0b' : '#dc2626'
            }}>
              {calcularMargenEstimado()}%
            </span>
          </div>
        </div>

        {/* Botones */}
        <div style={styles.actions}>
          <button
            type="button"
            onClick={onClose}
            style={styles.btnCancel}
            disabled={guardando}
          >
            Cancelar
          </button>
          <button
            type="submit"
            style={styles.btnSubmit}
            disabled={guardando}
          >
            {guardando ? '⏳ Guardando...' : '💾 Guardar Configuración'}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  container: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    maxWidth: '700px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '30px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #e5e7eb'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    color: '#1e293b',
    fontWeight: '700'
  },
  btnClose: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '5px 10px',
    lineHeight: '1'
  },
  loading: {
    textAlign: 'center',
    padding: '40px'
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 15px'
  },
  mensaje: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '600'
  },
  checkboxGroup: {
    background: '#f8fafc',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '25px',
    border: '2px solid #e2e8f0'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b'
  },
  checkbox: {
    width: '20px',
    height: '20px',
    marginRight: '12px',
    cursor: 'pointer'
  },
  checkboxText: {
    userSelect: 'none'
  },
  checkboxHelp: {
    margin: '8px 0 0 32px',
    fontSize: '13px',
    color: '#64748b'
  },
  section: {
    marginBottom: '25px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '15px'
  },
  formGroup: {
    marginBottom: '20px',
    position: 'relative'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569'
  },
  input: {
    width: '100%',
    padding: '12px 50px 12px 12px',
    fontSize: '16px',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'monospace'
  },
  currency: {
    position: 'absolute',
    right: '15px',
    top: '43px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b'
  },
  inputHelp: {
    margin: '6px 0 0 0',
    fontSize: '12px',
    color: '#64748b'
  },
  summary: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '25px'
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #cbd5e1'
  },
  summaryLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569'
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b'
  },
  actions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'flex-end'
  },
  btnCancel: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    background: 'white',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  btnSubmit: {
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  }
};

export default ConfiguracionFinanciera;